import '@justgiving/totaliser/src/Totaliser.scss';
import '@justgiving/totaliser/src/themes/Snowglobe/Snowglobe.scss';

import React, { FunctionComponent } from 'react';

import { Totaliser, Snowglobe, LondonEye, Pudsey } from '@justgiving/totaliser';

import { gql } from '@apollo/client';

import { TotaliserWrapperFragment } from '../../../types/__generated__';
import usePageQuery, { buildQuery } from '../../hooks/usePageQuery';
import TotaliserWrapperSkeleton from '../TotaliserWrapperSkeleton/TotaliserWrapperSkeleton';

import './TotaliserWrapper.scss';
import { useBasePageQuery, useNotNullableContext } from '../../hooks';
import { PageMetaContext } from '../../context';
import { isCharity } from '../../utils/typeGuards';

const FRAGMENT = buildQuery(gql`
  fragment TotaliserWrapper on Page {
    id
    targetWithCurrency {
      value
    }
    branding {
      primaryColor
    }
    reason {
      icon
    }
    relationships {
      events(first: 1) {
        nodes {
          legacyId
        }
      }
    }
  }
`);

const TotaliserWrapper: FunctionComponent = () => {
  const { pageType } = useNotNullableContext(PageMetaContext);
  const { loading, error, refetch, data = null } = usePageQuery<
    TotaliserWrapperFragment
  >(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });
  const { data: basePageData } = useBasePageQuery();

  if (loading) {
    return <TotaliserWrapperSkeleton animated displayRefreshButton={false} />;
  }

  if (error) {
    return <TotaliserWrapperSkeleton onRefresh={refetch} />;
  }

  if (!data || !basePageData) {
    return null;
  }

  const target = data.targetWithCurrency?.value;

  if (!target || target === 0) {
    return null;
  }

  const totalAmount =
    basePageData.page?.donationSummary?.totalAmount?.value ?? 0;
  const percentageOfTarget = (totalAmount / target) * 100;

  const beneficiaryNodes =
    basePageData.page?.relationships?.beneficiaries.nodes;
  const firstBeneficiary =
    beneficiaryNodes && beneficiaryNodes.length > 0
      ? beneficiaryNodes[0]
      : null;
  const isChildrenInNeedPage = Boolean(
    ['fundraising', 'campaign'].includes(pageType) &&
      firstBeneficiary &&
      isCharity(firstBeneficiary) &&
      firstBeneficiary.legacyId === '181821',
  );

  if (isChildrenInNeedPage) {
    return (
      <Totaliser
        className="jg-pages-totaliser-main"
        percent={percentageOfTarget}
        size="auto"
        isAnimated
      >
        <Pudsey />
      </Totaliser>
    );
  }

  const date = new Date();
  const month = date.getMonth();
  const day = date.getDate();
  const isXmas = month === 11 && day >= 15;

  const isBranded = data.branding?.primaryColor;
  const isInMemory = data.reason?.icon === 'inMemoryIcon';

  // LLHM 2023
  const isLLHM = data.relationships?.events?.nodes[0]?.legacyId === '7444724';

  return !isLLHM ? (
    <Totaliser
      className="jg-pages-totaliser-main"
      percent={percentageOfTarget}
      size="auto"
      isAnimated
    >
      {pageType === 'fundraising' && !isBranded && !isInMemory && isXmas ? (
        <Snowglobe />
      ) : null}
    </Totaliser>
  ) : (
    <Totaliser
      className="jg-pages-totaliser-main"
      percent={percentageOfTarget}
      size="auto"
      isAnimated
    >
      <LondonEye />
    </Totaliser>
  );
};

export default TotaliserWrapper;
